import { createContext, useContext } from 'react';
import type { ConversationPageFacade, PrototyperBuildTimeline } from '@ideoz/core';
import type { SnapshotIndexEntry } from '../../prototyper/utils/shouldStartBuildStream';
import type { ResumeBuildOutcome } from '../../prototyper/utils/resumeBuildGating';

/**
 * Web auth-dialog state passed into the conversation UI. Shape mirrors
 * `useAuthDialog` (web-only).
 */
export interface ConversationAuthDialogState {
  isOpen: boolean;
  mode: 'login' | 'register';
  isLoading: boolean;
  openRegister: () => void;
  openLogin: () => void;
  closeDialog: () => void;
  switchMode: (mode: 'login' | 'register') => void;
  handleLogin: (email: string, password: string) => Promise<void>;
  handleRegister: (name: string, email: string, password: string) => Promise<void>;
  handleGoogleAuth: () => Promise<void>;
  handleNavigateToForgotPassword: () => void;
}

/**
 * All WebContainer / Prototyper workbench wiring for the conversation UI,
 * bundled into one object so it's a single context field rather than ~25 drilled
 * props. Built (and mode-gated) by `ConversationPage`. Phase 3 will swap this for
 * a lazily-mounted value so clarify never pays the workbench cost.
 */
export interface ConversationWorkbench {
  renderWorkbenchPanel?: (props: {
    isOpen: boolean;
    onClose: () => void;
    onToggleConversationPanel: () => void;
    isConversationPanelCollapsed: boolean;
    /** When true, workbench fills legacy prototyper panel (no duplicate toolbar). */
    embedded?: boolean;
    /** Merged into the Workbench toolbar so the prototyper has a single header. */
    headerLeftSlot?: React.ReactNode;
    headerRightSlot?: React.ReactNode;
    headerRightLeadSlot?: React.ReactNode;
  }) => React.ReactNode;
  workbenchToolActivity?: React.ReactNode;
  isStreamingPrototype?: boolean;
  streamingPublishReady?: boolean;
  /** Why Publish is disabled right now (build running, unsaved edits, preview loading…); null once ready. */
  streamingPublishDisabledReason?: string | null;
  streamingPreviewReady?: boolean;
  onStreamingPrototypeStart?: (ctx?: { llmInteractionId?: string }) => void;
  workbenchSnapshotBootstrap?: 'pending' | 'restored' | 'none' | 'failed';
  workbenchSnapshotRestoreError?: string | null;
  workbenchSnapshotIndexLoading?: boolean;
  getWorkbenchSnapshotIndexEntry?: (llmInteractionId: string) => SnapshotIndexEntry | undefined;
  onRetrySnapshotBootstrap?: () => void;
  activeSnapshotInteractionId?: string | null;
  restoreInteractionLoadingId?: string | null;
  streamingWorkbenchLoading?: boolean;
  onStreamingWorkbenchPrototypePanelVisibilityChange?: (isOpen: boolean) => void;
  conversationHotEditSlot?: React.ReactNode;
  conversationHotEditActive?: boolean;
  streamingWorkbenchLlmInteractionId?: string | null;
  livePrototyperBuildTimeline?: PrototyperBuildTimeline | null;
  isWorkbenchBuildLive?: boolean;
  workbenchTimelineTerminalVisible?: boolean;
  onPrototyperWorkbenchRestore?: (
    llmInteractionId: string,
    hint?: { buildStatus?: string; hasBuildTimeline?: boolean }
  ) => void | Promise<void>;
  uploadVerifiedPrototypeSnapshot?: () => Promise<string | undefined>;
  /** Locally finish a build interrupted by refresh/lost tab (no LLM stream). */
  resumeInterruptedBuild?: (llmInteractionId: string) => Promise<ResumeBuildOutcome>;
  onCancelWorkbenchStream?: () => void;
  workbenchPersistError?: string | null;
}

/**
 * Everything the conversation UI needs, provided once by `ConversationPage`.
 * Replaces the 49-prop `ConversationModuleProps` drilling — descendants read
 * this via {@link useConversation} instead of receiving/re-passing props.
 */
export interface ConversationContextValue {
  facade: ConversationPageFacade;
  isWeb: boolean;
  urlConversationId?: string | null;
  authDialogState?: ConversationAuthDialogState;
  /** Scope back-navigation override (header back → Work Scopes). */
  onBack?: () => void;
  /** Scope 403/404 handler (re-resolve the scope instead of a full-screen 404). */
  onConversationNotFound?: () => void;
  workbench: ConversationWorkbench;
}

const ConversationContext = createContext<ConversationContextValue | null>(null);

export const ConversationProvider = ConversationContext.Provider;

/** Read the conversation context; throws if used outside `ConversationProvider`. */
export function useConversation(): ConversationContextValue {
  const ctx = useContext(ConversationContext);
  if (!ctx) {
    throw new Error('useConversation must be used within a ConversationProvider');
  }
  return ctx;
}
